import { useState } from "react";
import { ShieldCheck, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/components/badges";

const loadScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true); s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export async function startRazorpay(orderId, onPaid) {
  const { data: rp } = await api.post("/payments/razorpay/order", { order_id: orderId });
  if (rp.mode === "test_stub") return rp;
  const ok = await loadScript();
  if (!ok) throw new Error("Could not load Razorpay checkout");
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: rp.key_id, amount: rp.amount, currency: rp.currency, order_id: rp.razorpay_order_id,
      name: "MaterialXpress", description: `Order ${rp.order_no}`, prefill: rp.prefill, theme: { color: "#1d4ed8" },
      handler: async (res) => {
        try {
          const { data } = await api.post("/payments/razorpay/verify", {
            order_id: orderId, razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature,
          });
          onPaid(data); resolve(null);
        } catch (e) { reject(e); }
      },
      modal: { ondismiss: () => reject(new Error("Payment window closed — you can pay from the order page.")) },
    });
    rzp.open();
  });
}

export const TestPaymentModal = ({ rp, orderId, onPaid, onClose }) => {
  const [busy, setBusy] = useState(false);
  const pay = async (success) => {
    if (!success) { onClose(); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/razorpay/verify", {
        order_id: orderId, razorpay_order_id: rp.razorpay_order_id,
        razorpay_payment_id: `pay_test_${Date.now()}`, razorpay_signature: "test_stub",
      });
      onPaid(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="test-payment-modal">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-slate-900 text-white p-5">
          <p className="text-[10px] font-mono2 uppercase tracking-[0.2em] text-amber-400">Razorpay · Test mode</p>
          <p className="font-display text-xl font-bold mt-1">Pay {inr(rp.amount / 100)}</p>
          <p className="text-xs text-slate-400 mt-1">Order {rp.order_no}</p>
        </div>
        <div className="p-5 space-y-3">
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
            No Razorpay keys configured yet — this simulates the hosted checkout. Add <span className="font-mono2">RAZORPAY_KEY_ID/SECRET</span> to switch to live test cards.
          </div>
          <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-600 flex items-center gap-2"><CreditCard className="h-4 w-4" /> 4111 1111 1111 1111 · 12/29 · 123</div>
          <button onClick={() => pay(true)} disabled={busy} data-testid="test-pay-success" className="btn-brand w-full !py-3">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simulate successful payment"}</button>
          <button onClick={() => pay(false)} disabled={busy} data-testid="test-pay-cancel" className="btn-ghost-b2b w-full">Cancel — pay later</button>
          <p className="flex items-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Signature verified server-side before marking paid.</p>
        </div>
      </div>
    </div>
  );
};
